import { useMemo } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { formatVolume } from "@heraion/shared";

import { patternLabel } from "../patternLabel";
import { PanelTitleRow } from "./InfoPill";
import { ErrorCard, LoadingCard } from "./panelCards";
import { makeTierOneStyles } from "./panelStyles";
import { isEmptyReportPeriod, panelDays } from "./progressWindow";
import { panelMovementBalance, sliceRows, type RowSlice } from "./reportSectionRows";
import type { ProgressDeps } from "./types";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { weightUnitLabels } from "../weightUnitLabels";

// Sets per movement pattern, 7d + 28d. `slice`: one report page's rows.
export function MovementBalancePanel({ slice, ...deps }: ProgressDeps & { slice?: RowSlice }) {
  const { theme, units } = deps;
  const { range } = deps;
  const windowDays = panelDays(range);
  const { t, i18n } = useTranslation();
  // The translated unit word for every weight below (CN-26 ondata 2).
  const unitLabels = weightUnitLabels(t);
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);

  const data = useMemo(
    () => (results && catalog ? panelMovementBalance({ results, catalog, range }) : null),
    [results, catalog, range],
  );

  if (error) return <ErrorCard theme={theme} titleKey="balance" />;
  if (data === null) return <LoadingCard theme={theme} titleKey="balance" />;

  const balanceTitle = t("mobile.progress.balance.title", {
    defaultValue: "Movement balance",
  });
  const balanceHelp = t("mobile.progress.balance.help", {
    days: windowDays,
    defaultValue:
      'Sets and volume per movement pattern over {{days}} days, sorted by sets. The small "X/7d" number counts the last 7 of those days, useful to spot a single recent push (or a deliberate drop) that the longer window hides.',
  });
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  if (data.length === 0) {
    return (
      <View style={styles.card}>
        <PanelTitleRow
          theme={theme}
          title={balanceTitle}
          body={balanceHelp}
          period={period}
        />
        <Text style={styles.empty}>
          {/* PG-64: a report reads its own period, which may be the past. Only
              a period without sessions says so; sessions with nothing to
              draw keep Progress's sentence. */}
          {isEmptyReportPeriod(deps, results)
            ? t("mobile.progress.report.emptyPeriod", {
                defaultValue: "No training in this period.",
              })
            : t("mobile.progress.empty", {
                defaultValue: "No recent activity to compare",
              })}
        </Text>
      </View>
    );
  }

  // Over every row, so a page's bars compare with the other pages'.
  const max = Math.max(...data.map((r) => r.setsWindow));

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={balanceTitle}
        body={balanceHelp}
        period={period}
      />
      {sliceRows(data, slice).map((row) => {
        const fillPct = max === 0 ? 0 : (row.setsWindow / max) * 100;
        return (
          <View key={row.pattern} style={styles.balanceRow}>
            <View style={styles.balanceTextRow}>
              <Text style={styles.balanceLabel} numberOfLines={1}>
                {patternLabel(t, row.pattern)}
              </Text>
              <Text style={styles.balanceValue}>
                {row.setsWindow} · {row.sets7d}/7d ·{" "}
                {formatVolume(row.volumeWindowKg, units, i18n.language, unitLabels)}
              </Text>
            </View>
            <View style={styles.barTrack}>
              <View
                style={[styles.barFill, { width: `${Math.max(2, fillPct)}%` }]}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}
